import type { UseMutationResult } from '@tanstack/react-query';
import { Check, TriangleAlert, X } from 'lucide-react';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { formatDate } from '@/lib/utils/date.utils';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import type { BillingChange } from '@/lib/types/billing';
import { BILLING_STRINGS } from '../constants/billing-strings';

const COPY = BILLING_STRINGS.CANCEL;

export interface CancelSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The end of the paid period: the date access actually stops (A-74). */
  accessEndsAt: string | null;
  mutation: UseMutationResult<BillingChange, unknown, void>;
}

/**
 * Cancellation confirmation (FR-41, MYN-113.4).
 *
 * ## It states the date, and the date is not today
 *
 * A-74: cancelling schedules the end of the subscription at the end of the paid
 * period. The member keeps Premium until then and can reactivate right up to
 * that moment. A confirmation that said "your access will end" without naming
 * when would describe a product we do not sell - and the cancellation email
 * names that same date, so the screen and the mail must agree.
 *
 * ## Lost and kept are both listed
 *
 * From the design, and worth keeping: the fear at this moment is that
 * cancelling deletes the startup. Naming what survives (profile, account, team)
 * beside what stops (data room, seats) answers that directly. Share links are
 * free-tier and survive the lapse, so they are listed as kept.
 *
 * ## "Keep" is the green button
 *
 * As in the design: the safe choice is the inviting one, and comes first in
 * tab order (ConfirmDialog's stacked layout).
 */
export function CancelSubscriptionDialog({
  open,
  onOpenChange,
  accessEndsAt,
  mutation,
}: CancelSubscriptionDialogProps) {
  const endDate = accessEndsAt ? formatDate(accessEndsAt) : null;
  const outcomes = [
    { text: COPY.LOSE_DAXE, kept: false },
    { text: COPY.LOSE_SEATS, kept: false },
    { text: COPY.KEEP_PROFILE, kept: true },
    { text: COPY.KEEP_ACCOUNT, kept: true },
    { text: COPY.KEEP_LINKS, kept: true },
  ];

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={COPY.TITLE}
      description={COPY.BODY}
      confirmLabel={COPY.CONFIRM}
      cancelLabel={COPY.DISMISS}
      cancelVariant="green"
      layout="stacked"
      align="center"
      headerIcon={
        <span className="bg-red/10 flex h-14 w-14 items-center justify-center rounded-full">
          <TriangleAlert aria-hidden="true" className="text-red h-6 w-6" />
        </span>
      }
      isConfirming={mutation.isPending}
      onConfirm={() => mutation.mutate(undefined, { onSuccess: () => onOpenChange(false) })}
    >
      <div className="flex flex-col gap-4 text-left">
        {/* Lost and kept in one list, each marked: the fear at this moment is
            that cancelling deletes the startup, and the ticks answer it. */}
        <ul className="bg-surface-raised rounded-card flex flex-col gap-2.5 p-4 text-sm">
          {outcomes.map((outcome) => (
            <li key={outcome.text} className="text-text-primary flex items-start gap-2.5">
              {outcome.kept ? (
                <Check aria-label={COPY.KEEP_TITLE} className="text-green mt-0.5 h-4 w-4 shrink-0" />
              ) : (
                <X aria-label={COPY.LOSE_TITLE} className="text-red mt-0.5 h-4 w-4 shrink-0" />
              )}
              <span>{outcome.text}</span>
            </li>
          ))}
        </ul>

        {endDate && (
          <div className="text-center">
            <p className="text-text-primary text-sm font-semibold">
              {COPY.ENDS_ON.replace('{date}', endDate)}
            </p>
            <p className="text-text-muted mt-1 text-xs">
              {COPY.REACTIVATE_NOTE.replace('{date}', endDate)}
            </p>
          </div>
        )}

        {mutation.isError && (
          <p role="alert" className="text-danger text-sm">
            {getApiErrorMessage(mutation.error, COPY.CONFIRM)}
          </p>
        )}
      </div>
    </ConfirmDialog>
  );
}
